import moment from 'moment'
import React, { useCallback, useMemo } from 'react'
import { EventProps, momentLocalizer, Views } from 'react-big-calendar'
// @ts-ignore
import 'react-big-calendar/lib/css/react-big-calendar.css'
import { useTranslation } from 'react-i18next'
import { ICalendarEvent } from 'src/generated-types/calendarEvent'
import i18n from '../../i18n'
import { useCalendarTypeStyle } from './calendarColors'
import { EventHover, EventSegments } from './CalendarEventContent'
import { getEventTextMaxWidth, useAvailableWidth } from './calendarLayout'
import CalendarWeekView from './CalendarWeekView'
import { CalendarScroller, EventContentBox, StyledCalendar } from './GlobalCalendar.style'

// Re-exported for the popover/content modules that import it from here.
export { calendarTypeColor, useCalendarTypeStyle } from './calendarColors'

const localizer = momentLocalizer(moment)

interface GlobalCalendarProps {
  events: ICalendarEvent[]
  onSelectEvent: (event: ICalendarEvent, mouseEvent?: React.MouseEvent | MouseEvent) => void
  onMonthChange: (date: Date) => void
}

const GlobalCalendar = ({ events, onSelectEvent, onMonthChange }: GlobalCalendarProps) => {
  const { t } = useTranslation(['common', 'task'])
  const getTypeStyle = useCalendarTypeStyle()
  const [scrollerRef, availableWidth] = useAvailableWidth<HTMLDivElement>()
  const eventTextMaxWidth = availableWidth ? getEventTextMaxWidth(availableWidth) : undefined

  const calendarTranslations = {
    next: t('next', { ns: 'common' }),
    previous: t('back', { ns: 'common' }),
    today: t('today', { ns: 'task' }),
    month: t('month', { ns: 'common' }),
    week: t('week', { ns: 'common' }),
  }

  const eventPropGetter = (event: ICalendarEvent) => {
    const { tint, text, accent } = getTypeStyle(event.type)
    return {
      style: {
        backgroundColor: tint,
        color: text,
        // Projected/planned occurrences get a dashed accent and a slightly faded look.
        borderLeft: `3px ${event.isProjected ? 'dashed' : 'solid'} ${accent}`,
        borderRadius: 7,
        fontWeight: 600,
        opacity: event.isProjected ? 0.72 : 1,
      },
    }
  }

  // react-big-calendar passes the originating click event as the second argument,
  // which is the reliable source for positioning the popover and detecting modifier keys.
  const handleEventClick = useCallback(
    (event: ICalendarEvent, e: React.SyntheticEvent) => {
      const overlay = document.querySelector('.rbc-overlay') as HTMLElement
      if (overlay) overlay.style.display = 'none'
      onSelectEvent(event, e as unknown as React.MouseEvent)
    },
    [onSelectEvent],
  )

  const calendarComponents = useMemo(
    () => ({
      event: (props: EventProps<ICalendarEvent>) => (
        <CalendarEventContent {...props} maxTextWidth={eventTextMaxWidth} />
      ),
    }),
    [eventTextMaxWidth],
  )

  return (
    <CalendarScroller ref={scrollerRef}>
      <StyledCalendar
        culture={i18n.language}
        startAccessor={(event: ICalendarEvent) => new Date(event.start)}
        endAccessor={(event: ICalendarEvent) => {
          const endDate = event.end ? new Date(event.end) : new Date(event.start)
          endDate.setDate(endDate.getDate() + 1)
          return endDate
        }}
        localizer={localizer}
        events={events}
        onSelectEvent={handleEventClick}
        onNavigate={(date: Date) => onMonthChange(date)}
        defaultView={Views.WEEK}
        views={{ week: CalendarWeekView, month: true }}
        popup
        popupOffset={{ x: 2, y: 10 }}
        eventPropGetter={eventPropGetter}
        components={calendarComponents}
        messages={calendarTranslations}
      />
    </CalendarScroller>
  )
}

interface ICalendarEventContentProps {
  event?: ICalendarEvent
  maxTextWidth?: number
}

const CalendarEventContent = ({ event, maxTextWidth }: ICalendarEventContentProps) => {
  if (!event) return null
  return (
    <EventHover event={event}>
      <EventContentBox>
        <EventSegments event={event} maxTextWidth={maxTextWidth} />
      </EventContentBox>
    </EventHover>
  )
}

export default GlobalCalendar
